import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function ArtAdvisorPremiumDemo() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const features = [
    {
      icon: "♾️",
      title: "Collections clients illimitées",
      description: "Aucune limite au nombre de clients que vous pouvez accompagner. Scalez votre activité sans contrainte.",
      demo: "Dashboard avec vue sur 10, 20, 50+ collections actives"
    },
    {
      icon: "🤖",
      title: "IA pour tous vos clients",
      description: "Chaque collection bénéficie de l'analyse IA complète : conservation, matching successoral, veille marché.",
      demo: "Rapports IA automatisés pour l'ensemble de votre portefeuille client"
    },
    {
      icon: "🎨",
      title: "Co-marquage avancé",
      description: "Rapports, exports et interfaces avec votre identité visuelle complète (logo, couleurs, typographie).",
      demo: "Éditeur de thème visuel avec prévisualisation en temps réel"
    },
    {
      icon: "📈",
      title: "Tableau de bord agrégé",
      description: "Vue d'ensemble de tous vos clients : valeurs totales, tendances, alertes consolidées.",
      demo: "Analytics cross-collections avec graphiques comparatifs"
    },
    {
      icon: "🔌",
      title: "API & intégrations",
      description: "Connectez Artkiv à vos outils métier (CRM, comptabilité, communication) via notre API REST.",
      demo: "Documentation API complète et webhooks configurables"
    },
    {
      icon: "🎯",
      title: "Support dédié 24/7",
      description: "Un account manager personnel + hotline technique disponible 7j/7 avec SLA garanti.",
      demo: "Ligne directe dédiée et Slack privé avec l'équipe Artkiv"
    }
  ];

  const useCases = [
    {
      title: "Cabinet d'art advisory établi",
      profile: "15-30 clients fortunés",
      needs: "Reporting régulier, analyses de marché, recommandations d'acquisition",
      solution: "Centralisation complète, rapports automatisés co-marqués, veille proactive"
    },
    {
      title: "Multi-family office",
      profile: "5-10 familles avec collections importantes",
      needs: "Gestion patrimoniale, succession, conformité fiscale multi-juridictions",
      solution: "Vue consolidée, IA successorale, exports réglementaires personnalisés"
    },
    {
      title: "Galerie avec service advisory",
      profile: "20-50 collectionneurs suivis",
      needs: "Suivi post-vente, recommandations personnalisées, événements",
      solution: "CRM artistique intégré, scoring des opportunités, communications ciblées"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-black via-neutral-900 to-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-white text-black text-sm font-bold rounded-full mb-6">
              {t("demo.advisorPremium.badge")}
            </div>
            <h1 
              className="text-5xl md:text-7xl font-serif mb-6 tracking-tight"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("demo.advisorPremium.hero.title")}
            </h1>
            <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
              {t("demo.advisorPremium.hero.subtitle")}
            </p>
            <div className="flex items-baseline justify-center gap-2 mb-8">
              <span className="text-6xl font-bold">{t("demo.advisorPremium.price")}</span>
              <span className="text-3xl text-neutral-400">
                {t("pricing.period.month")}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate("./contact?plan=art_advisor_premium")}
                className="px-10 py-4 bg-white text-black rounded-full font-semibold hover:bg-neutral-200 transition-all duration-300 text-lg"
              >
               {t("demo.advisorPremium.cta.demo")}
              </button>
              <button
                onClick={() => navigate("./compare-plans")}
                className="px-10 py-4 border-2 border-white text-white rounded-full font-semibold hover:bg-white hover:text-black transition-all duration-300 text-lg"
              >
                {t("demo.advisorPremium.cta.compare")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Différenciateurs clés */}
      <div className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-serif mb-4"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Pourquoi passer Premium ?
            </h2>
            <p className="text-xl text-neutral-500">
              Les avantages qui font toute la différence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl border-2 border-black">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-3">Scaling illimité</h3>
              <p className="text-neutral-600 leading-relaxed">
                Passez de 5 à 50 clients sans changer d'outil. Votre plateforme
                grandit avec votre activité.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border-2 border-black">
              <div className="text-5xl mb-4">🎨</div>
              <h3 className="text-2xl font-bold mb-3">White-label complet</h3>
              <p className="text-neutral-600 leading-relaxed">
                Vos clients ne voient que votre marque. Rapports, emails, interfaces
                co-marqués à 100%.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border-2 border-black">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold mb-3">Automatisation IA</h3>
              <p className="text-neutral-600 leading-relaxed">
                L'IA travaille pour tous vos clients : analyses, alertes, recommandations
                générées automatiquement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features détaillées */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl font-serif mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Fonctionnalités Premium
          </h2>
          <p className="text-xl text-neutral-500">
            Tout ce que contient le plan Art Advisor, et plus encore
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {features.map((feature, idx) => (
            <div key={idx} className="group">
              <div className="mb-4 text-5xl">{feature.icon}</div>
              <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                {feature.description}
              </p>
              <div className="p-4 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl border border-neutral-200">
                <p className="text-sm text-neutral-500 italic">{feature.demo}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Use Cases */}
      <div className="bg-black text-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-serif mb-4"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {t("demo.advisorPremium.usecases.title")}
            </h2>
            <p className="text-xl text-neutral-400">
              {t("demo.advisorPremium.usecases.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, idx) => (
              <div key={idx} className="bg-neutral-900 p-8 rounded-2xl border border-neutral-800">
                <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-neutral-400 mb-1">Profil</p>
                    <p className="text-white">{useCase.profile}</p>
                  </div>
                  <div>
                    <p className="text-neutral-400 mb-1">Besoins</p>
                    <p className="text-neutral-300">{useCase.needs}</p>
                  </div>
                  <div>
                    <p className="text-neutral-400 mb-1">Solution Artkiv</p>
                    <p className="text-green-400">{useCase.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-10 border border-indigo-200">
          <h3 
            className="text-3xl font-serif mb-6 text-center"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {t("demo.advisorPremium.roi.title")}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white p-6 rounded-xl">
              <h4 className="font-bold text-lg mb-4">Sans Artkiv Premium</h4>
              <ul className="space-y-3 text-sm text-neutral-600">
                <li>• 10h/semaine de reporting manuel</li>
                <li>• Outils éparpillés (Excel, Dropbox, Email)</li>
                <li>• Risques d'erreurs et données obsolètes</li>
                <li>• Difficile de scaler au-delà de 10 clients</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <p className="text-xs text-neutral-500">Coût temps : ~500€/mois en heures facturables</p>
              </div>
            </div>

            <div className="bg-black text-white p-6 rounded-xl">
              <h4 className="font-bold text-lg mb-4">Avec Artkiv Premium</h4>
              <ul className="space-y-3 text-sm">
                <li>• Rapports automatisés en 1 clic</li>
                <li>• Plateforme centralisée tout-en-un</li>
                <li>• IA qui travaille 24/7 pour vous</li>
                <li>• Capacité de gérer 50+ clients facilement</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-neutral-700">
                <p className="text-xs text-neutral-300">Coût : 199€/mois • Gain temps : ~300€/mois</p>
                <p className="text-green-400 font-bold mt-2">ROI positif dès le 1er mois</p>
              </div>
            </div>
          </div>

          <p className="text-center text-neutral-600 text-sm">
            * Calculs basés sur un taux horaire art advisor de 150€/h
          </p>
        </div>
      </div>

      {/* Témoignage premium */}
      <div className="bg-neutral-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-12 border-2 border-black">
            <div className="text-6xl mb-6 text-center">⭐⭐⭐⭐⭐</div>
            <p className="text-2xl font-serif text-center mb-6 italic leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>
              "Artkiv Premium a transformé mon cabinet. Je gère maintenant 28 clients
              avec la même sérénité que j'en gérais 8 avant. Le co-marquage fait
              toute la différence auprès de ma clientèle fortunée."
            </p>
            <div className="text-center">
              <p className="font-semibold">Alexandra D.</p>
              <p className="text-sm text-neutral-500">Fondatrice, AD Art Advisory, Genève</p>
              <p className="text-xs text-neutral-400 mt-2">Portfolio géré : 42M€</p>
            </div>
          </div>
        </div>
      </div>

{/* API Section - EN COURS DE DÉVELOPPEMENT */}
      <div className="max-w-6xl mx-auto px-6 py-20 opacity-60">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold mb-4">
              <span>🚧</span>
              <span>EN COURS DE DÉVELOPPEMENT</span>
            </div>
            <h2 
              className="text-4xl font-serif mb-6 italic"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Intégrations & API
            </h2>
            <p className="text-lg text-neutral-600 mb-6 leading-relaxed italic">
              Bientôt : connectez Artkiv à votre écosystème existant. Notre API REST vous permettra
              d'automatiser les workflows, synchroniser les données et créer des
              expériences sur-mesure.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3">
                <span className="text-2xl grayscale">🔗</span>
                <div>
                  <p className="font-semibold italic">Webhooks en temps réel</p>
                  <p className="text-sm text-neutral-500 italic">Notifications instantanées sur tous les événements</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl grayscale">📱</span>
                <div>
                  <p className="font-semibold italic">SDK & Libraries</p>
                  <p className="text-sm text-neutral-500 italic">Python, Node.js, PHP pour démarrer rapidement</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl grayscale">🛡️</span>
                <div>
                  <p className="font-semibold italic">Sécurité Enterprise</p>
                  <p className="text-sm text-neutral-500 italic">OAuth 2.0, rate limiting, logs d'audit complets</p>
                </div>
              </li>
            </ul>
            <button
              disabled
              className="px-6 py-3 border-2 border-neutral-300 text-neutral-400 rounded-full cursor-not-allowed italic"
            >
              Documentation API (à venir)
            </button>
          </div>
          <div className="bg-neutral-100 text-neutral-400 p-8 rounded-2xl font-mono text-sm border-2 border-dashed border-neutral-300">
            <pre className="overflow-x-auto italic">
{`// Exemple : Créer une œuvre via API
// (Fonctionnalité en développement)

fetch('https://api.artkiv.com/v1/artworks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    title: "Sans titre",
    artist_id: "artist_123",
    client_id: "client_456",
    acquisition_date: "2024-01-15",
    acquisition_price: 15000
  })
});`}
            </pre>
          </div>
        </div>
      </div>

      {/* CTA Final */}
      <div className="bg-gradient-to-b from-white to-neutral-50 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 
            className="text-5xl font-serif mb-6"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {t("demo.advisorPremium.final.title")}
          </h2>
          <p className="text-xl text-neutral-500 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t("demo.advisorPremium.final.subtitle")}
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <button
              onClick={() => navigate("/contact?plan=art_advisor_premium&demo=true")}
              className="px-10 py-5 bg-black text-white rounded-full font-semibold hover:bg-neutral-800 transition-all duration-300 text-lg"
            >
              {t("demo.advisorPremium.final.cta.demo")}
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="px-10 py-5 border-2 border-neutral-300 text-neutral-700 rounded-full font-semibold hover:border-black hover:text-black transition-all duration-300 text-lg"
            >
              {t("demo.advisorPremium.final.cta.plans")}
            </button>
          </div>
          <p className="text-sm text-neutral-500">
            {t("demo.advisorPremium.final.note")}
          </p>
        </div>
      </div>
    </div>
  );
}